import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { create } from 'zustand';

const ALLOWED_DOMAINS = ['glossika.com'];
const ALLOWED_EMAILS = ['glossika@gmail.com'];

export function isAllowedEmail(email: string): boolean {
  const normalized = email.trim().toLowerCase();
  if (ALLOWED_EMAILS.includes(normalized)) return true;
  const at = normalized.lastIndexOf('@');
  return at > 0 && ALLOWED_DOMAINS.includes(normalized.slice(at + 1));
}

const pool = new CognitoUserPool({
  UserPoolId: import.meta.env.VITE_COGNITO_POOL_ID ?? 'ap-northeast-1_Ti6uAjngG',
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? '5gcausebgpvtnocmmbsffjtud',
});

interface AuthUser {
  email: string;
  sub: string;
  groups: string[];
}

interface AuthState {
  cognitoUser: CognitoUser | null;
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  refreshSession: () => Promise<void>;
}

function extractUser(session: CognitoUserSession): AuthUser {
  const payload = session.getIdToken().decodePayload();
  return {
    email: (payload['email'] as string) ?? '',
    sub: (payload['sub'] as string) ?? '',
    groups: (payload['cognito:groups'] as string[]) ?? [],
  };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  cognitoUser: null,
  user: null,
  token: null,
  loading: true,
  error: null,

  signIn: (email: string, password: string) =>
    new Promise<void>((resolve, reject) => {
      if (!isAllowedEmail(email)) {
        const err = new Error('Access restricted to @glossika.com emails');
        set({ error: err.message, loading: false });
        reject(err);
        return;
      }

      const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session: CognitoUserSession) => {
          const user = extractUser(session);
          set({
            cognitoUser,
            user,
            token: session.getIdToken().getJwtToken(),
            loading: false,
            error: null,
          });
          resolve();
        },
        onFailure: (err: Error) => {
          set({ cognitoUser: null, user: null, token: null, loading: false, error: err.message });
          reject(err);
        },
      });
    }),

  signOut: () => {
    const { cognitoUser } = get();
    if (cognitoUser) cognitoUser.signOut();
    set({ cognitoUser: null, user: null, token: null, loading: false, error: null });
  },

  refreshSession: () =>
    new Promise<void>((resolve) => {
      const currentUser = pool.getCurrentUser();
      if (!currentUser) {
        set({ cognitoUser: null, user: null, token: null, loading: false });
        resolve();
        return;
      }

      currentUser.getSession(
        (err: Error | null, session: CognitoUserSession | null) => {
          if (err || !session) {
            set({ cognitoUser: null, user: null, token: null, loading: false });
            resolve();
            return;
          }

          const user = extractUser(session);
          if (!isAllowedEmail(user.email)) {
            currentUser.signOut();
            set({ cognitoUser: null, user: null, token: null, loading: false, error: 'Access restricted' });
            resolve();
            return;
          }

          set({
            cognitoUser: currentUser,
            user,
            token: session.getIdToken().getJwtToken(),
            loading: false,
            error: null,
          });
          resolve();
        },
      );
    }),
}));
